import { Typography } from "@king-ict/eupisi-ui/components/Typography/Typography";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import StaticField from "../../components/StaticField";
import { ProgramType } from "../../models/Program/ProgramType";
import { LearningSet } from "../../models/ProgramRequest/LearningSet";
import { Module } from "../../models/ProgramRequest/Module";
import { ModuleInfo } from "../ProgramRequestEdit/ModuleInfo";
import styles from "./ProgramRequestDetails.module.scss";

interface IProps {
  name: string;
  modules: Module[];
  programTypeId: number;
  correctionModules?: Module[];
  qualificationTypeId?: number;
  readonly?: boolean;
  getErrorForPath: (path: string) => string | undefined;
  onChange: (e: any) => void;
}

/** In readonly mode, displays a text header row and several rows 
 * for each module including learning sets. If there are no modules, 
 * nothing is rendered. 
 * In editable mode, displays 
 * a ModuleInfo component for adding/removing modules and 
 * learning sets.
 */
export const ProgramRequestModulePanel = ({
  name,
  modules,
  programTypeId,
  correctionModules,
  qualificationTypeId = 0,
  readonly = false,
  getErrorForPath,
  onChange,
}: IProps): JSX.Element => {
  const { t } = useTranslation();

  return (
    <>
      {readonly && modules &&
        <>
          <Row>
            <Col md={12} className={styles.rowTitleSpacing}>
              <Typography variant="smallHeading" component="h3">
                {t("request.modules")}
              </Typography>
            </Col>
          </Row>
          {!modules.length && (
            <div className={styles.modulePanel}>
              <Row>
                <Col md={12}>
                  <StaticField
                    label={t("request.modules")}
                    value={t("common.noData")}
                    className={styles.changedFieldModule}
                  />
                </Col>
              </Row>
            </div>
          )}
          {modules.map((module: Module, index: number) => {
            const requestCorrectionModule = correctionModules && correctionModules.length > index ? correctionModules[index] : undefined;
            return (
              <div key={module.id ?? `module-${index}`} className={styles.modulePanel}>
                <Row>
                  <Col md={12}>
                    <StaticField
                      label={t("request.moduleName")}
                      value={module.name}
                      newValue={requestCorrectionModule?.name}
                      className={styles.changedFieldModule}
                    />
                  </Col>
                </Row>
                {module.learningSets.map((learningSet: LearningSet, index: number) => (
                  <Row key={learningSet.id ?? `${module.id ?? `module-${index}`}-ls-${index}`}>
                    <Col md={12}>
                      <StaticField
                        label={t("request.moduleLearningSetName")}
                        value={learningSet.name}
                        newValue={requestCorrectionModule && requestCorrectionModule.learningSets.length > index ?
                          requestCorrectionModule.learningSets[index].name : ""}
                        className={styles.changedFieldModule}
                      />
                    </Col>
                    <Col md={4}>
                      <StaticField
                        label={t("request.moduleLearningSetCode")}
                        value={learningSet.code}
                        newValue={requestCorrectionModule && requestCorrectionModule.learningSets.length > index ?
                          requestCorrectionModule.learningSets[index].code : ""}
                        className={styles.changedFieldModule}
                      />
                    </Col>
                    <Col md={4}>
                      <StaticField
                        label={t("request.moduleLearningSetLevel")}
                        value={learningSet.level}
                        newValue={requestCorrectionModule && requestCorrectionModule.learningSets.length > index ?
                          requestCorrectionModule.learningSets[index].level : ""}
                        className={styles.changedFieldModule}
                      />
                    </Col>
                    <Col md={4}>
                      <StaticField
                        label={t("request.moduleLearningSetCSVET")}
                        value={learningSet.csvet}
                        newValue={requestCorrectionModule && requestCorrectionModule.learningSets.length > index ?
                          requestCorrectionModule.learningSets[index].csvet : ""}
                        className={styles.changedFieldModule}
                      />
                    </Col>
                  </Row>
                ))}
              </div>
            )
          })}
        </>
      }
      {(!readonly
        && (programTypeId === ProgramType.TrainingPrograms
          || programTypeId === ProgramType.CraftsAndArtsTrainingPrograms
          || programTypeId === ProgramType.CraftsSpecializationPrograms
          || programTypeId === ProgramType.RefinementPrograms          
          || programTypeId === ProgramType.NewVocationalCurriculum
        )) &&
        <>
          <ModuleInfo
            name={"modules"}
            getErrorForPath={getErrorForPath}
            modules={modules}
            qualificationType={qualificationTypeId}
            onChange={onChange} />
        </>
      }
    </>
  );
};